<template>
  <div class="ai-assistant" v-if="isLoggedIn && !isPublicPage">
    <!-- Floating Button -->
    <div class="ai-fab" @click="toggleChat" :class="{ 'ai-fab-active': isOpen }">
      <svg v-if="!isOpen" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 2a8 8 0 0 1 8 8v1a8 8 0 0 1-8 8H4l1.76-2.64A8 8 0 0 1 4 11v-1a8 8 0 0 1 8-8z"/>
        <circle cx="9.5" cy="10" r="1" fill="currentColor" stroke="none"/>
        <circle cx="14.5" cy="10" r="1" fill="currentColor" stroke="none"/>
        <path d="M9.5 14a3.5 3.5 0 0 0 5 0"/>
        <path d="M7 2.5c0-1 .5-1.5 1.5-1.5"/>
        <path d="M17 2.5c0-1-.5-1.5-1.5-1.5"/>
      </svg>
      <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </div>

    <!-- Chat Window -->
    <transition name="ai-slide">
      <div class="ai-chat-window" v-if="isOpen">
        <div class="ai-chat-header">
          <div class="ai-chat-header-info">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2a8 8 0 0 1 8 8v1a8 8 0 0 1-8 8H4l1.76-2.64A8 8 0 0 1 4 11v-1a8 8 0 0 1 8-8z"/>
              <circle cx="9.5" cy="10" r="1" fill="currentColor" stroke="none"/>
              <circle cx="14.5" cy="10" r="1" fill="currentColor" stroke="none"/>
            </svg>
            <span>سعود - المساعد الذكي</span>
          </div>
          <button class="ai-chat-close" @click="toggleChat">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="ai-chat-body" ref="chatBody">
          <!-- Welcome message -->
          <div class="ai-message ai-message-bot" v-if="messages.length === 0">
            <div class="ai-message-content">
              مرحباً! أنا سعود، المساعد الذكي لنظام تحضير. كيف أقدر أساعدك؟
            </div>
          </div>

          <div v-for="(msg, index) in messages" :key="index"
               :class="['ai-message', msg.role === 'user' ? 'ai-message-user' : 'ai-message-bot']">
            <div class="ai-message-content" v-if="!msg.display.includes('OKK')">
              <div v-if="msg.isVoice && msg.role === 'user'" class="ai-voice-badge">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
                رسالة صوتية
              </div>
              <div v-html="formatMessage(msg.display)"></div>
              <div v-if="msg.action === 'navigate' && msg.role === 'assistant'" class="ai-action-btn-wrap">
                <button class="btn btn-sm btn-primary mt-1" @click="executeAction(msg)">
                  <i class="fa fa-arrow-left"></i>
                  انتقل الآن
                </button>
              </div>
            </div>
          </div>

          <!-- Loading -->
          <div class="ai-message ai-message-bot" v-if="loading">
            <div class="ai-message-content ai-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>

        <div class="ai-chat-footer">
          <form @submit.prevent="sendMessage">
            <div class="ai-input-wrap">
              <input
                type="text"
                v-model="userInput"
                placeholder="...اكتب أو اضغط المايك"
                :disabled="loading || recording"
                ref="chatInput"
                autocomplete="off"
              />
              <!-- Voice Record Button -->
              <button type="button" class="ai-mic-btn" :class="{ 'ai-mic-recording': recording }" @click="toggleRecording" :disabled="loading">
                <svg v-if="!recording" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="2"/>
                </svg>
              </button>
              <!-- Send Button -->
              <button type="submit" :disabled="loading || recording || !userInput.trim()">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
            <div v-if="recording" class="ai-recording-indicator">
              <span class="ai-rec-dot"></span> جاري التسجيل... ({{ recordingTime }}ث)
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'AiAssistant',
  data() {
    var saved = {}
    try { saved = JSON.parse(localStorage.getItem('ai_assistant_chat') || '{}') } catch(e) {}
    return {
      isOpen: false,
      userInput: '',
      messages: saved.messages || [],
      history: saved.history || [],
      loading: false,
      recording: false,
      recordingTime: 0,
      mediaRecorder: null,
      audioChunks: [],
      recordingTimer: null
    }
  },
  watch: {
    messages: {
      handler: function() { this.saveChat() },
      deep: true
    }
  },
  mounted() {
    // Check if there's auto-fill data from a previous navigation
    var g = this
    try {
      var autofill = JSON.parse(localStorage.getItem('ai_autofill') || 'null')
      if (autofill && autofill.data && (Date.now() - autofill.time) < 30000) {
        window.__aiAssistantPage = autofill.page
        setTimeout(function() { g.autoFill(autofill.data) }, 2000)
        setTimeout(function() { g.autoFill(autofill.data) }, 4000)
        setTimeout(function() {
          g.autoFill(autofill.data)
          localStorage.removeItem('ai_autofill')
        }, 6000)
      } else {
        localStorage.removeItem('ai_autofill')
      }
    } catch(e) {}
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('user')
    },
    isPublicPage() {
      var path = window.location.pathname
      var publicPaths = ['/login', '/register', '/reset', '/logout', '/error-404', '/system_stopped', '/_xteacher']
      for (var i = 0; i < publicPaths.length; i++) {
        if (path.startsWith(publicPaths[i])) return true
      }
      return false
    }
  },
  methods: {
    saveChat() {
      try {
        localStorage.setItem('ai_assistant_chat', JSON.stringify({
          messages: this.messages.slice(-50),
          history: this.history.slice(-20)
        }))
      } catch(e) {}
    },
    toggleChat() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        var g = this
        this.$nextTick(function() {
          if (g.$refs.chatInput) {
            g.$refs.chatInput.focus()
          }
          g.scrollToBottom()
        })
      }
    },
    scrollToBottom() {
      this.$nextTick(function() {
        var body = this.$refs.chatBody
        if (body) {
          body.scrollTop = body.scrollHeight
        }
      })
    },
    async toggleRecording() {
      if (this.recording) {
        this.stopRecording()
      } else {
        this.startRecording()
      }
    },
    async startRecording() {
      try {
        var stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.audioChunks = []
        this.mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
        var g = this

        this.mediaRecorder.ondataavailable = function(e) {
          if (e.data.size > 0) {
            g.audioChunks.push(e.data)
          }
        }

        this.mediaRecorder.onstop = function() {
          stream.getTracks().forEach(function(t) { t.stop() })
          var blob = new Blob(g.audioChunks, { type: 'audio/webm' })
          g.sendVoice(blob)
        }

        this.mediaRecorder.start()
        this.recording = true
        this.recordingTime = 0
        this.recordingTimer = setInterval(function() {
          g.recordingTime++
          if (g.recordingTime >= 60) {
            g.stopRecording()
          }
        }, 1000)
      } catch(e) {
        alert('لم يتم السماح باستخدام المايكروفون')
      }
    },
    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop()
      }
      this.recording = false
      clearInterval(this.recordingTimer)
    },
    async sendVoice(blob) {
      this.messages.push({ role: 'user', content: 'رسالة صوتية', display: 'رسالة صوتية (' + this.recordingTime + ' ثانية)', isVoice: true })
      this.loading = true
      this.scrollToBottom()

      var g = this
      try {
        var user = JSON.parse(localStorage.getItem('user'))

        // Convert blob to base64
        var reader = new FileReader()
        reader.onloadend = function() {
          var base64 = reader.result.split(',')[1]

          $.ajax({
            url: api + '/user/assistant/chat-voice',
            type: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({
              jwt: user.jwt,
              audio: base64,
              history: g.history.slice(-6)
            }),
            success: function(data) {
              g.loading = false
              g.handleResponse(data)
            },
            error: function() {
              g.loading = false
              g.messages.push({ role: 'assistant', display: 'عذراً، لم أتمكن من معالجة الصوت.' })
              g.scrollToBottom()
            }
          })
        }
        reader.readAsDataURL(blob)
      } catch(e) {
        this.loading = false
        this.messages.push({ role: 'assistant', display: 'حدث خطأ في إرسال الصوت.' })
        this.scrollToBottom()
      }
    },
    handleResponse(data) {
      var g = this
      if (data.status === 100 && data.response) {
        var resp = data.response
        var displayMsg = resp.message || 'تم'
        var msgObj = {
          role: 'assistant',
          content: displayMsg,
          display: displayMsg,
          action: resp.action || null,
          route: resp.route || null,
          page: resp.page || null,
          data: resp.data || null
        }

        g.messages.push(msgObj)
        g.history.push({ role: 'assistant', content: displayMsg })

        if (resp.action === 'navigate' && resp.route) {
          setTimeout(function() {
            g.executeAction(msgObj)
          }, 1500)
        }
      } else {
        g.messages.push({
          role: 'assistant',
          display: data.response || 'عذراً، حدث خطأ'
        })
      }
      g.scrollToBottom()
      g.$nextTick(function() { if (g.$refs.chatInput) g.$refs.chatInput.focus() })
    },
    async sendMessage() {
      var text = this.userInput.trim()
      if (!text || this.loading) return

      this.messages.push({ role: 'user', content: text, display: text })
      this.history.push({ role: 'user', content: text })
      this.userInput = ''
      this.loading = true
      this.scrollToBottom()

      var g = this
      try {
        var user = JSON.parse(localStorage.getItem('user'))

        $.post(api + '/user/assistant/chat', {
          jwt: user.jwt,
          message: text,
          history: JSON.stringify(g.history.slice(-6))
        }).then(function(data) {
          g.loading = false
          g.handleResponse(data)
        }).fail(function() {
          g.loading = false
          g.messages.push({ role: 'assistant', display: 'عذراً، لم أتمكن من الاتصال.' })
          g.scrollToBottom()
        })
      } catch(e) {
        this.loading = false
        this.messages.push({ role: 'assistant', display: 'حدث خطأ غير متوقع.' })
        this.scrollToBottom()
      }
    },
    executeAction(msg) {
      if (msg.action === 'navigate' && msg.route) {
        var data = msg.data || {}

        // Store auto-fill data in localStorage before navigation
        if (Object.keys(data).length > 0) {
          localStorage.setItem('ai_autofill', JSON.stringify({
            data: data,
            page: msg.page || '',
            time: Date.now()
          }))
        }

        this.isOpen = false
        window.location.href = msg.route
      }
    },
    formatMessage(text) {
      if (!text) return ''
      // Extract all URLs first, replace with placeholders
      var links = []
      var clean = text
      // Match URLs with protocol
      clean = clean.replace(/(https?:\/\/[^\s]+)/g, function(match) {
        links.push(match)
        return '{{LINK' + (links.length - 1) + '}}'
      })
      // Match URLs without protocol (e.g. tahdir.net, landing.tahdir.net)
      clean = clean.replace(/((?:[\w-]+\.)+(?:net|com|org|sa|io|co)(?:\/[^\s]*)?)/g, function(match) {
        links.push('https://' + match)
        return '{{LINK' + (links.length - 1) + '}}'
      })
      // Escape HTML
      clean = clean.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // Restore links as clickable <a> tags
      for (var i = 0; i < links.length; i++) {
        var displayUrl = links[i].replace(/^https?:\/\//, '')
        clean = clean.replace('{{LINK' + i + '}}', '<a href="' + links[i] + '" target="_blank" style="color:#4fc3f7;text-decoration:underline;word-break:break-all;">' + displayUrl + '</a>')
      }
      // Convert newlines to <br>
      clean = clean.replace(/\n/g, '<br>')
      return clean
    },
    autoFill(data) {
      var page = window.__aiAssistantPage || ''

      // Helper: set Vue v-model value on an input/textarea/select
      function setVueValue(selector, value) {
        var el = $(selector)
        if (!el.length) return false
        var domEl = el[0]
        // For select elements
        if (domEl.tagName === 'SELECT') {
          // Try exact match first
          var found = false
          el.find('option').each(function() {
            if ($(this).val() === value || $(this).text().trim() === value) {
              el.val($(this).val())
              found = true
              return false
            }
          })
          // Try partial match
          if (!found) {
            el.find('option').each(function() {
              if ($(this).val().includes(value) || $(this).text().includes(value)) {
                el.val($(this).val())
                found = true
                return false
              }
            })
          }
          if (found) {
            domEl.dispatchEvent(new Event('input', { bubbles: true }))
            domEl.dispatchEvent(new Event('change', { bubbles: true }))
          }
          return found
        }
        // For input/textarea
        var nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          domEl.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype, 'value'
        ).set
        nativeInputValueSetter.call(domEl, value)
        domEl.dispatchEvent(new Event('input', { bubbles: true }))
        domEl.dispatchEvent(new Event('change', { bubbles: true }))
        return true
      }

      // Helper: click checkbox that contains text
      function clickCheckbox(containerSelector, text) {
        var found = false
        $(containerSelector).each(function() {
          var label = $(this).text().trim()
          if (label.includes(text) || text.includes(label)) {
            var checkbox = $(this).find('input[type="checkbox"]')
            if (checkbox.length && !checkbox.prop('checked')) {
              checkbox.click()
              found = true
            }
          }
        })
        return found
      }

      // Page-specific auto-fill logic
      if (page === 'students-messages' || window.location.pathname === '/students-messages') {
        // Set way to bulk if classroom specified
        if (data.classroom) {
          // Click "اختر من الفصول" button
          $("button:contains('اختر من الفصول'), span:contains('اختر من الفصول')").closest('button').click()
          setTimeout(function() {
            // Find and click the classroom checkbox
            clickCheckbox('.custom-control, .custom-checkbox, label', data.classroom)
          }, 500)
        }
        if (data.message) {
          setTimeout(function() {
            setVueValue('textarea.asd43345, textarea[placeholder*="الرسالة"]', data.message)
          }, 800)
        }
      }

      if (page === 'students-reports-daily' || window.location.pathname.includes('/reports/daily')) {
        if (data.date) setVueValue('#ddate, input[type="date"]', data.date)
        if (data.classname) {
          setTimeout(function() {
            // classname format: "أولى-1" or "أولى-all"
            setVueValue('select', data.classname)
          }, 500)
        }
        if (data.status) setVueValue('select:eq(1)', data.status)
        if (data.q) setVueValue('#qq, input[placeholder*="ابحث"]', data.q)
      }

      if (page === 'students-reports-between' || window.location.pathname.includes('/reports/between')) {
        if (data.from) setVueValue('input[type="date"]:first', data.from)
        if (data.to) setVueValue('input[type="date"]:last', data.to)
        if (data.classname) {
          setTimeout(function() { setVueValue('select:first', data.classname) }, 500)
        }
        if (data.ghiab_count) setVueValue('input[type="number"]:first', data.ghiab_count)
        if (data.late_count) setVueValue('input[type="number"]:last', data.late_count)
      }

      if (page === 'students-scan-bulk' || window.location.pathname.includes('/scan/bulk')) {
        if (data.date) setVueValue('input[type="date"]', data.date)
        if (data.classroom) {
          setTimeout(function() {
            clickCheckbox('.custom-control, .ddd, label', data.classroom)
          }, 500)
        }
      }

      if (page === 'students-alerts' || page === 'students-alerts2') {
        if (data.from) setVueValue('input[type="date"]:first', data.from)
        if (data.to) setVueValue('input[type="date"]:last', data.to)
        if (data.limit) setVueValue('select:first', data.limit)
        if (data.classroom) {
          setTimeout(function() { setVueValue('select', data.classroom) }, 500)
        }
      }

      if (page === 'teachers-send' || window.location.pathname === '/_teachers/send') {
        if (data.message) {
          setVueValue('textarea[placeholder*="الرسالة"], textarea:first', data.message)
        }
      }

      if (page === 'teachers-mails-send' || window.location.pathname.includes('/mails/send')) {
        if (data.title) setVueValue('input[placeholder*="عنوان"], input:first', data.title)
        if (data.content) setVueValue('textarea:first', data.content)
        if (data.message) setVueValue('textarea:last', data.message)
      }

      if (page === 'teachers-reports-daily' || window.location.pathname === '/_teachers/reports/daily') {
        if (data.date) setVueValue('input[type="date"]', data.date)
      }

      if (page === 'teachers-reports-between' || window.location.pathname === '/_teachers/reports/between') {
        if (data.from) setVueValue('input[type="date"]:first', data.from)
        if (data.to) setVueValue('input[type="date"]:last', data.to)
      }

      if (page === 'lessons-report' || window.location.pathname.includes('/_lessons/report')) {
        if (data.date) setVueValue('input[type="date"]', data.date)
        if (data.classroom) {
          setTimeout(function() { setVueValue('select:first', data.classroom) }, 500)
        }
      }

      if (page === 'students-list' || window.location.pathname === '/_students/list') {
        if (data.q) setVueValue('input[placeholder*="ابحث"], input[type="text"]:first', data.q)
      }

      if (page === 'mentors-create' || window.location.pathname.includes('/_mentors/create')) {
        if (data.title) setVueValue('input[placeholder*="عنوان"], input[type="text"]:first', data.title)
        if (data.description) setVueValue('textarea:first', data.description)
      }

      // Clear after last attempt
      if (window.__aiAutoFillCount === undefined) window.__aiAutoFillCount = 0
      window.__aiAutoFillCount++
      if (window.__aiAutoFillCount >= 3) {
        window.__aiAutoFillCount = 0
        window.__aiAssistantData = null
        window.__aiAssistantPage = null
      }
    }
  }
}
</script>

<style scoped>
.ai-assistant {
  position: fixed;
  bottom: 20px;
  right: 20px;
  left: auto;
  z-index: 99999;
  font-family: 'Cairo', 'Segoe UI', Tahoma, sans-serif;
  direction: rtl;
}

.ai-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(115, 103, 240, 0.4);
  transition: all 0.3s ease;
  font-size: 22px;
  position: relative;
}
.ai-fab:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(115, 103, 240, 0.6);
}
.ai-fab-active {
  background: linear-gradient(135deg, #ea5455 0%, #f08182 100%);
  box-shadow: 0 4px 15px rgba(234, 84, 85, 0.4);
}

.ai-chat-window {
  position: absolute;
  bottom: 70px;
  right: 0;
  left: auto;
  width: 380px;
  max-height: 520px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (max-width: 480px) {
  .ai-chat-window {
    width: calc(100vw - 40px);
    max-height: 70vh;
    right: -10px;
    left: auto;
  }
}

.ai-chat-header {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ai-chat-header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
}
.ai-chat-close {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  opacity: 0.8;
  padding: 0;
  display: flex;
}
.ai-chat-close:hover { opacity: 1; }

.ai-chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 300px;
  max-height: 380px;
  background: #f8f8fb;
}

.ai-message {
  margin-bottom: 12px;
  display: flex;
}
.ai-message-user { justify-content: flex-end; }
.ai-message-bot { justify-content: flex-start; }
.ai-message-content {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13.5px;
  line-height: 1.8;
  word-wrap: break-word;
  direction: rtl;
  text-align: left;
}
.ai-message-user .ai-message-content {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.ai-message-bot .ai-message-content {
  background: #fff;
  color: #333;
  border: 1px solid #e8e8ed;
  border-bottom-left-radius: 4px;
}

.ai-voice-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  opacity: 0.8;
  margin-bottom: 4px;
}

.ai-action-btn-wrap { margin-top: 6px; }
.ai-action-btn-wrap .btn {
  font-size: 12px;
  padding: 3px 12px;
  border-radius: 8px;
}

.ai-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 18px;
}
.ai-typing span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7367f0;
  animation: ai-bounce 1.4s infinite both;
}
.ai-typing span:nth-child(2) { animation-delay: 0.16s; }
.ai-typing span:nth-child(3) { animation-delay: 0.32s; }

@keyframes ai-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

.ai-chat-footer {
  padding: 12px;
  border-top: 1px solid #eee;
  background: #fff;
}
.ai-input-wrap {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  background: #f4f4f8;
  border-radius: 24px;
  padding: 4px 14px 4px 4px;
  gap: 6px;
}
.ai-input-wrap input {
  flex: 1;
  border: none;
  background: none;
  outline: none;
  font-size: 14px;
  padding: 0px 8px;
  color: #333;
  direction: rtl;
  text-align: left;
}
.ai-input-wrap input::placeholder { color: #aaa; }
.ai-input-wrap button[type="submit"] {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;
  flex-shrink: 0;
}
.ai-input-wrap button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ai-mic-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #e8e8ed;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.ai-mic-btn:hover { background: #ddd; }
.ai-mic-recording {
  background: #ea5455 !important;
  color: #fff !important;
  animation: ai-pulse 1.5s infinite;
}

@keyframes ai-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(234, 84, 85, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(234, 84, 85, 0); }
}

.ai-recording-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #ea5455;
  margin-top: 8px;
  padding-right: 8px;
}
.ai-rec-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ea5455;
  animation: ai-blink 1s infinite;
}
@keyframes ai-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.ai-slide-enter-active, .ai-slide-leave-active {
  transition: all 0.3s ease;
}
.ai-slide-enter, .ai-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}
</style>
